import { useEffect, useState } from 'react';

/**
 * Stato della connessione, con verifica attiva.
 *
 * `navigator.onLine` è notoriamente ottimista: segnala "online" anche quando la
 * scheda di rete è collegata ma il server è irraggiungibile. Per questo lo stato
 * riportato è confermato da una richiesta reale, così l'indicatore in sala dice
 * la verità invece di rassicurare a vuoto.
 */
export function useOnlineStatus(): { isOnline: boolean; lastCheckedAt: Date | null } {
  const [isOnline, setIsOnline] = useState(() => navigator.onLine);
  const [lastCheckedAt, setLastCheckedAt] = useState<Date | null>(null);

  useEffect(() => {
    let cancelled = false;

    const verify = async (): Promise<void> => {
      if (!navigator.onLine) {
        if (!cancelled) {
          setIsOnline(false);
          setLastCheckedAt(new Date());
        }
        return;
      }
      try {
        // Richiesta minima verso una risorsa dell'origine, non memorizzabile in
        // cache: conferma che il server risponde davvero.
        await fetch('/health.json', { method: 'HEAD', cache: 'no-store' });
        if (!cancelled) setIsOnline(true);
      } catch {
        if (!cancelled) setIsOnline(false);
      } finally {
        if (!cancelled) setLastCheckedAt(new Date());
      }
    };

    const handleOnline = () => void verify();
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    void verify();

    const interval = window.setInterval(() => void verify(), 30_000);

    return () => {
      cancelled = true;
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.clearInterval(interval);
    };
  }, []);

  return { isOnline, lastCheckedAt };
}
